/**
 * Shared cache + fetch for public display pricing (PBI #1193).
 * Max SPA cache: 5 minutes (under the 30-minute staleness budget; matches API ResponseCache).
 */

import { useEffect, useState } from 'react';
import { usePublicSubscriptionConfigOverride } from '../contexts/PublicSubscriptionConfigOverrideContext';
import { getPublicSubscriptionConfig } from '../services/publicSubscriptionConfigService';
import type { PublicSubscriptionConfig } from '../types/publicSubscriptionConfig';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedConfig: PublicSubscriptionConfig | null = null;
let cachedAtMs = 0;
let inFlight: Promise<PublicSubscriptionConfig> | null = null;

/**
 * Synchronous read of the in-memory cache (may be null before first fetch).
 */
export function getCachedPublicSubscriptionConfig(): PublicSubscriptionConfig | null {
  if (!cachedConfig) {
    return null;
  }

  if (Date.now() - cachedAtMs >= CACHE_TTL_MS) {
    return null;
  }

  return cachedConfig;
}

/**
 * Returns cached config when fresh; otherwise fetches and caches.
 */
export async function loadPublicSubscriptionConfig(
  forceRefresh = false,
): Promise<PublicSubscriptionConfig> {
  const now = Date.now();
  if (!forceRefresh && cachedConfig && now - cachedAtMs < CACHE_TTL_MS) {
    return cachedConfig;
  }

  if (!inFlight) {
    inFlight = getPublicSubscriptionConfig()
      .then((config) => {
        cachedConfig = config;
        cachedAtMs = Date.now();
        return config;
      })
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
}

/**
 * Clears the module cache (tests / after admin price changes in-session).
 */
export function clearPublicSubscriptionConfigCache(): void {
  cachedConfig = null;
  cachedAtMs = 0;
  inFlight = null;
}

/**
 * Hook that loads public display pricing once and shares the module cache.
 */
export function usePublicSubscriptionConfig(): {
  config: PublicSubscriptionConfig | null;
  loading: boolean;
  error: Error | null;
} {
  const override = usePublicSubscriptionConfigOverride();
  const [config, setConfig] = useState<PublicSubscriptionConfig | null>(
    override ?? getCachedPublicSubscriptionConfig(),
  );
  const [loading, setLoading] = useState(!override && !getCachedPublicSubscriptionConfig());
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (override) {
      setConfig(override);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    loadPublicSubscriptionConfig()
      .then((value) => {
        if (!cancelled) {
          setConfig(value);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [override]);

  return { config, loading, error };
}
